<template>
  <div class="space-y-6">
    <div class="flex items-center justify-between">
      <h3 class="text-xl font-semibold text-slate-800">Mi horario semanal</h3>
      <button
        class="px-4 py-2 rounded bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
        :disabled="guardando || !hayCambios"
        @click="guardarPlantilla"
      >
        {{ guardando ? "Guardando..." : "Guardar cambios" }}
      </button>
    </div>
    <div
      v-if="estado.mensaje"
      class="text-sm px-3 py-2 rounded-lg"
      :class="estado.tipo === 'error' ? 'bg-red-50 text-red-600' : 'bg-emerald-50 text-emerald-600'"
    >
      {{ estado.mensaje }}
    </div>

    <div class="grid gap-4 md:grid-cols-2">
      <div
        v-for="dia in dias"
        :key="dia.id"
        class="border rounded-lg bg-white shadow-sm p-4 space-y-3"
      >
        <div class="flex items-center justify-between">
          <div>
            <p class="text-lg font-semibold capitalize">{{ dia.label }}</p>
            <p class="text-xs text-slate-500">Formato 12 horas (AM/PM)</p>
          </div>
          <label class="inline-flex items-center cursor-pointer">
            <input type="checkbox" class="sr-only" v-model="plantilla[dia.id].activo" />
            <span
              class="w-11 h-6 bg-gray-200 rounded-full flex items-center p-1 transition"
              :class="plantilla[dia.id].activo ? 'bg-emerald-500' : 'bg-gray-300'"
            >
              <span
                class="w-4 h-4 bg-white rounded-full shadow transform transition"
                :class="plantilla[dia.id].activo ? 'translate-x-5' : 'translate-x-0'"
              ></span>
            </span>
          </label>
        </div>

        <div v-if="plantilla[dia.id].activo" class="space-y-3">
          <div
            v-for="(intervalo, idx) in plantilla[dia.id].intervalos"
            :key="`${dia.id}-${idx}`"
            class="bg-slate-50 rounded-lg p-3 space-y-2"
          >
            <div class="flex items-center justify-between text-xs text-slate-500">
              <span>Intervalo {{ idx + 1 }}</span>
              <button
                class="text-red-500 hover:underline"
                @click="eliminarIntervalo(dia.id, idx)"
                v-if="plantilla[dia.id].intervalos.length > 1"
              >
                Eliminar
              </button>
            </div>
            <div class="grid grid-cols-2 gap-3">
              <IntervalSelector
                :valor="intervalo.desde"
                label="Desde"
                @update="(val) => actualizarIntervalo(dia.id, idx, 'desde', val)"
              />
              <IntervalSelector
                :valor="intervalo.hasta"
                label="Hasta"
                @update="(val) => actualizarIntervalo(dia.id, idx, 'hasta', val)"
              />
            </div>
          </div>
          <button
            class="w-full text-sm border border-dashed border-slate-300 rounded py-2 text-slate-600 hover:bg-slate-100"
            @click="agregarIntervalo(dia.id)"
          >
            + Añadir intervalo
          </button>
        </div>
        <p v-else class="text-xs text-slate-400">Día pausado</p>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch } from "vue";
import { collection, doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "@/firebase";
import IntervalSelector from "./HorarioPlantillaIntervalo.vue";

const DIAS = [
  { id: "lunes", label: "lunes" },
  { id: "martes", label: "martes" },
  { id: "miercoles", label: "miércoles" },
  { id: "jueves", label: "jueves" },
  { id: "viernes", label: "viernes" },
  { id: "sabado", label: "sábado" },
  { id: "domingo", label: "domingo" }
];

function crearIntervalo() {
  return {
    desde: { hora: 8, minuto: 0, ampm: "AM" },
    hasta: { hora: 5, minuto: 0, ampm: "PM" }
  };
}

function crearPlantillaBase() {
  const base = {};
  for (const dia of DIAS) {
    base[dia.id] = {
      activo: false,
      intervalos: [crearIntervalo()]
    };
  }
  return base;
}

export default {
  name: "HorarioPlantilla",
  components: { IntervalSelector },
  emits: ["plantilla-guardada"],
  props: {
    usuarioId: {
      type: String,
      required: true
    }
  },
  setup(props, { emit }) {
    const dias = DIAS;
    const plantilla = reactive(crearPlantillaBase());
    const original = ref(null);
    const guardando = ref(false);
    const estado = ref({ mensaje: "", tipo: "info" });
    let estadoTimeout = null;

    const hayCambios = computed(() => {
      return JSON.stringify(plantilla) !== JSON.stringify(original.value);
    });

    function mostrarEstado(mensaje, tipo = "success") {
      estado.value = { mensaje, tipo };
      if (estadoTimeout) clearTimeout(estadoTimeout);
      estadoTimeout = setTimeout(() => {
        estado.value.mensaje = "";
      }, 4000);
    }

    async function cargarPlantilla() {
      if (!props.usuarioId) return;
      const docRef = doc(collection(db, "horarios"), props.usuarioId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const datos = snap.data().plantillaSemanal;
        if (datos) {
          for (const dia of dias) {
            const dataDia = datos[dia.id] || {};
            plantilla[dia.id].activo = Boolean(dataDia.activo);
            plantilla[dia.id].intervalos = Array.isArray(dataDia.intervalos) && dataDia.intervalos.length
              ? dataDia.intervalos
              : [crearIntervalo()];
          }
          original.value = JSON.parse(JSON.stringify(plantilla));
          return;
        }
      }
      original.value = JSON.parse(JSON.stringify(plantilla));
    }

    function agregarIntervalo(diaId) {
      plantilla[diaId].intervalos.push(crearIntervalo());
    }

    function eliminarIntervalo(diaId, idx) {
      const arr = plantilla[diaId].intervalos;
      if (arr.length <= 1) return;
      arr.splice(idx, 1);
    }

    function actualizarIntervalo(diaId, idx, campo, valor) {
      plantilla[diaId].intervalos[idx][campo] = valor;
    }

    function validarPlantilla() {
      const activos = Object.values(plantilla).filter((d) => d.activo);
      if (activos.length === 0) {
        return "Debes activar al menos un día de trabajo.";
      }
      for (const [diaId, data] of Object.entries(plantilla)) {
        if (!data.activo) continue;
        for (const intervalo of data.intervalos) {
          const minutosDesde = convertirAMinutos(intervalo.desde);
          const minutosHasta = convertirAMinutos(intervalo.hasta);
          if (minutosDesde >= minutosHasta) {
            return `El intervalo de ${diaId} debe tener una hora de fin mayor a la inicial.`;
          }
        }
      }
      return "";
    }

    function convertirAMinutos({ hora, minuto, ampm }) {
      let h = Number(hora);
      if (ampm === "PM" && h !== 12) h += 12;
      if (ampm === "AM" && h === 12) h = 0;
      return h * 60 + Number(minuto);
    }

    async function guardarPlantilla() {
      const error = validarPlantilla();
      if (error) {
        mostrarEstado(error, "error");
        return;
      }
      guardando.value = true;
      try {
        const payload = JSON.parse(JSON.stringify(plantilla));
        await setDoc(
          doc(collection(db, "horarios"), props.usuarioId),
          { plantillaSemanal: payload },
          { merge: true }
        );
        original.value = JSON.parse(JSON.stringify(plantilla));
        emit("plantilla-guardada", payload);
        mostrarEstado("Horario actualizado correctamente.");
      } catch (err) {
        console.error("Error guardando plantilla:", err);
        mostrarEstado("No se pudo guardar la plantilla. Intenta nuevamente.", "error");
      } finally {
        guardando.value = false;
      }
    }

    watch(
      () => props.usuarioId,
      () => {
        const base = crearPlantillaBase();
        for (const dia of dias) {
          plantilla[dia.id].activo = base[dia.id].activo;
          plantilla[dia.id].intervalos = base[dia.id].intervalos.map((intervalo) =>
            JSON.parse(JSON.stringify(intervalo))
          );
        }
        original.value = JSON.parse(JSON.stringify(plantilla));
        cargarPlantilla();
      },
      { immediate: true }
    );

    return {
      dias,
      plantilla,
      guardando,
      hayCambios,
      estado,
      agregarIntervalo,
      eliminarIntervalo,
      actualizarIntervalo,
      guardarPlantilla
    };
  }
};
</script>
